// src/api/client.ts
/**
 * Centralized Axios instance used by all API hooks.
 * It attaches the JWT access token to every request, handles token refresh on 401,
 * and maps server error responses to a unified error shape.
 */
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { defaultApiClientConfig, ApiClientConfig } from './config';
import { getAuthState, getRawAuthState, setAuthState } from './utils/tokenRefreshContext';
import { mapErrorResponse } from './utils/errorMapper';
import { startRefresh, enqueueRefresh, isRefreshInProgress } from './utils/tokenRefreshContext';

// Create a single Axios instance that will be configured at runtime.
// Export it immediately so that imports receive a live reference.
export const api: AxiosInstance = axios.create();

interface PivotAxiosRequestConfig extends AxiosRequestConfig {
  skipAuth?: boolean;
  skipTokenRefresh?: boolean;
  _retry?: boolean;
}

// Guard to ensure interceptors are added only once, even if initApiClient is called multiple times.
let interceptorsAdded = false;

/**
 * Initialise the API client. Should be called once (e.g., in the app entry point).
 * Allows overriding the default configuration (e.g., baseUrl from env).
 */
export function initApiClient(customConfig?: Partial<ApiClientConfig>): AxiosInstance {
  const config: ApiClientConfig = {
    ...defaultApiClientConfig,
    ...customConfig,
  };

  // Apply configuration to the already‑created instance.
  api.defaults.baseURL = config.baseUrl;
  api.defaults.timeout = config.timeoutMs;
  api.defaults.headers = { ...api.defaults.headers, ...config.defaultHeaders };

  console.log('[ApiClient] Initialized with baseURL:', config.baseUrl);

  // Add interceptors only once to avoid duplicate handlers.
  if (!interceptorsAdded) {
    // Request interceptor – attach Authorization header if we have a token (including expired)
    api.interceptors.request.use((req: PivotAxiosRequestConfig) => {
      const auth = getRawAuthState(); // use raw state to ensure header is sent even if token is expired
      const hasToken = !!auth?.accessToken;
      console.log('[ApiClient Request]', {
        method: req.method?.toUpperCase(),
        url: req.url,
        hasAuth: hasToken,
        tokenExpiry: auth?.expiresAt?.toISOString(),
        skipAuth: !!req.skipAuth,
      } as any);
      
      if (!req.skipAuth && auth?.accessToken) {
        req.headers = req.headers ?? {};
        // eslint-disable-next-line @typescript-eslint/naming-convention
        (req.headers as any)['Authorization'] = `Bearer ${auth.accessToken}`;
      }
      return req;
    });

    // Response interceptor – handle 401 (token expired) and map errors
    api.interceptors.response.use(
      (response: AxiosResponse) => {
        console.log('[ApiClient Response]', {
          method: response.config?.method?.toUpperCase(),
          url: response.config?.url,
          status: response.status,
        });
        return response;
      },
      async (error: AxiosError) => {
        const status = error.response?.status;
        const url = error.config?.url;
        const method = error.config?.method?.toUpperCase();
        
        console.log('[ApiClient Error]', {
          method,
          url,
          status,
          message: error.message,
        });

        // If we get a 401, attempt token refresh (if we have a refresh token)
        if (status === 401) {
          const originalRequest = error.config as PivotAxiosRequestConfig | undefined;
          if (originalRequest?.skipTokenRefresh || originalRequest?._retry) {
            console.log('[ApiClient 401] Token refresh skipped for request:', originalRequest?.url);
            return Promise.reject(mapErrorResponse(error));
          }

          const auth = getRawAuthState();
          console.log('[ApiClient 401] Has refresh token:', !!auth?.refreshToken);
          
          if (auth?.refreshToken) {
            if (originalRequest) {
              originalRequest._retry = true;
            }
            const retryRequest = async () => {
              const originalRequest = error.config as PivotAxiosRequestConfig;
              if (originalRequest.headers && 'Authorization' in originalRequest.headers) {
                delete (originalRequest.headers as any)['Authorization'];
              }
              console.log('[ApiClient 401] Retrying request:', originalRequest.url);
              return api.request(originalRequest);
            };

            // If a refresh is already in progress, queue the retry until it finishes.
            if (isRefreshInProgress()) {
              console.log('[ApiClient 401] Refresh in progress, queuing request');
              return enqueueRefresh(retryRequest);
            }

            // No refresh in progress – start one.
            console.log('[ApiClient 401] Starting token refresh');
            const finishRefresh = startRefresh();
            try {
              await refreshAccessToken();
              finishRefresh();
              console.log('[ApiClient 401] Token refresh successful, retrying request');
              return retryRequest();
            } catch (refreshError) {
              // Refresh failed – clear auth state and propagate original error
              console.log('[ApiClient 401] Token refresh failed:', refreshError);
              finishRefresh();
              setAuthState(null);
              return Promise.reject(mapErrorResponse(error));
            }
          } else {
            console.log('[ApiClient 401] No refresh token available, redirect to login');
            // No refresh token - user needs to log in again
            setAuthState(null);
            return Promise.reject(mapErrorResponse(error));
          }
        }
        // For other errors, map to a consistent shape
        return Promise.reject(mapErrorResponse(error));
      },
    );
    interceptorsAdded = true;
  }

  return api;
}
/**
 * Helper that performs a token refresh request.
 * This function is deliberately isolated so that the tokenRefreshContext can
 * serialize concurrent refresh attempts.
 */
async function refreshAccessToken(): Promise<void> {
  // Use raw auth state to obtain the refresh token even if the access token is expired.
  const { refreshToken } = getRawAuthState() ?? {};
  if (!refreshToken) {
    console.log('[RefreshToken] No refresh token available');
    throw new Error('No refresh token available');
  }
  // Determine the base URL to use for the refresh request. Prefer the base URL of the
  // configured api instance (which has been set in initApiClient).
  const baseUrl = api.defaults.baseURL || defaultApiClientConfig.baseUrl;
  console.log('[RefreshToken] Refreshing token with baseURL:', baseUrl);
  // Use a plain axios instance without interceptors to avoid recursion
  const plain = axios.create({ baseURL: baseUrl });
  try {
    const response = await plain.post('/auth/refresh', { refreshToken });
    const data = response.data as { accessToken: string; refreshToken: string; expiresIn: number; user?: any };
    console.log('[RefreshToken] Received new tokens, expiresIn:', data.expiresIn);
    // Update auth state with new tokens and calculated expiry
    const expiresAt = new Date(Date.now() + data.expiresIn * 1000);
    const newAuthState = {
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      expiresAt,
      user: data.user ?? getAuthState()?.user ?? null,
      isAuthenticated: true,
    };
    // Update both in-memory state and localStorage
    setAuthState(newAuthState);
    // Also update localStorage for persistence across page reloads
    if (typeof window !== 'undefined') {
      try {
        const localStorageData = {
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          expiresAt: expiresAt.toISOString(),
          user: data.user ?? getAuthState()?.user ?? null,
        };
        localStorage.setItem('auth', JSON.stringify(localStorageData));
        console.log('[RefreshToken] Updated localStorage with new tokens');
      } catch (e) {
        console.error('[RefreshToken] Failed to update localStorage:', e);
      }
    }
  } catch (error) {
    console.error('[RefreshToken] Refresh request failed:', error);
    throw error;
  }
}

// Note: No automatic initialisation of `api` here. Consumers must call `initApiClient`
// before using the exported `api` instance. This design avoids premature creation with
// an empty base URL, which caused integration test failures.